import React from "react";
import Marquee from "react-fast-marquee";
import { CheckCircle2, Sparkles } from "lucide-react";
import { Reveal } from "./motion";

const BrowserFrame = ({ children }) => (
  <div className="rounded-2xl border border-navy-100 bg-white p-2.5 shadow-xl dark:border-white/10 dark:bg-navy-800">
    <div className="mb-2 flex items-center gap-1.5 px-2 pt-1">
      <span className="h-2.5 w-2.5 rounded-full bg-navy-100 dark:bg-white/20" />
      <span className="h-2.5 w-2.5 rounded-full bg-navy-100 dark:bg-white/20" />
      <span className="h-2.5 w-2.5 rounded-full bg-navy-100 dark:bg-white/20" />
    </div>
    <div className="overflow-hidden rounded-xl">{children}</div>
  </div>
);

const PhoneFrame = ({ children }) => (
  <div className="mx-auto w-[240px] overflow-hidden rounded-[2.2rem] border-[7px] border-navy-800 bg-navy-800 shadow-2xl">
    {children}
  </div>
);

const blocks = [
  {
    n: "01",
    tag: "Cadastro com Talos Vision",
    title: "Escaneie. Fotografe. Está no ar.",
    desc:
      'Aponte a câmera para o código de barras — o MUFT reconhece o produto, preenche nome, marca, categoria e sugere toda a estrutura fiscal. Ou clique em "Preencher com IA" e deixe o Talos fazer o trabalho por você.',
    bullets: [
      "Scanner de EAN pela câmera do dispositivo",
      "Talos identifica nome, marca e categoria por foto",
      "Sugestão fiscal completa (NCM, CEST, CFOP, IBS/CBS)",
      "Funciona em modo manual quando faltar conexão",
    ],
    media: (
      <BrowserFrame>
        <img
          src="/assets/muft/cadastro_produto.png"
          alt="Cadastro de produto com IA no MUFT"
          className="w-full object-cover"
          data-testid="showcase-cadastro-image"
        />
      </BrowserFrame>
    ),
    reverse: false,
  },
  {
    n: "02",
    tag: "PDV · Frente de Caixa",
    title: "Venda, desconto e cliente — em três toques.",
    desc:
      "O carrinho do MUFT foi feito pra ir rápido. Atalhos de desconto (+1%, +5%, +10%), vinculação de cliente por CPF/CNPJ, subtotal ao vivo e prosseguir direto pro pagamento. Estoque e financeiro atualizados na hora.",
    bullets: [
      "Atalhos rápidos de desconto (%, R$ ou sem desconto)",
      "Vinculação de venda ao cliente com um toque",
      "Subtotal e desconto calculados ao vivo",
      "Múltiplas formas de pagamento e NF-e imediata",
    ],
    media: (
      <PhoneFrame>
        <img
          src="/assets/muft/pdv_carrinho.png"
          alt="Carrinho do PDV MUFT"
          className="w-full object-cover"
          data-testid="showcase-pdv-image"
        />
      </PhoneFrame>
    ),
    reverse: true,
  },
  {
    n: "03",
    tag: "Configuração Fiscal & NF-e",
    title: "SEFAZ sem medo. Enquadramento guiado.",
    desc:
      "Configure Simples Nacional, Regime Normal, alíquotas de ICMS/PIS/COFINS e certificado digital A1 direto do celular. O Talos valida se o cadastro está pronto para emissão antes de enviar pra SEFAZ.",
    bullets: [
      "Enquadramento fiscal por produto ou categoria",
      "Certificado digital A1 com validação na hora",
      "XMLs e DANFEs organizados e exportáveis em lote",
      "Talos explica rejeições em português simples",
    ],
    media: (
      <PhoneFrame>
        <img
          src="/assets/muft/config_fiscal.jpeg"
          alt="Configuração fiscal no MUFT"
          className="w-full object-cover"
          data-testid="showcase-fiscal-image"
        />
      </PhoneFrame>
    ),
    reverse: false,
  },
  {
    n: "04",
    tag: "Ponto Digital & Biometria",
    title: "Selfie, GPS e geocerca. Sem fraudes.",
    desc:
      "O colaborador bate o ponto com selfie e o MUFT valida a biometria facial contra a foto cadastrada, cruzando com a localização da filial. Relatórios consolidados pro RH em um clique.",
    bullets: [
      "Reconhecimento facial com verificação ao vivo",
      "Geolocalização com cerca virtual por filial",
      "Espelho de ponto mensal e banco de horas",
      "100% adequado à Portaria 671 do MTP",
    ],
    media: (
      <PhoneFrame>
        <img
          src="/assets/muft/ponto_digital.jpeg"
          alt="Ponto digital no MUFT"
          className="w-full object-cover"
          data-testid="showcase-ponto-image"
        />
      </PhoneFrame>
    ),
    reverse: true,
  },
];

export const Showcase = () => {
  return (
    <section
      id="showcase"
      className="relative bg-white py-24 transition-colors duration-500 dark:bg-navy-950 md:py-32"
      data-testid="showcase-section"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <Reveal className="mx-auto mb-20 max-w-3xl text-center">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-navy-100 bg-lavender/40 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.25em] text-navy-800 dark:border-white/10 dark:bg-navy-800 dark:text-lavender">
            <Sparkles className="h-3.5 w-3.5" /> Telas do Aplicativo Real
          </div>
          <h2 className="text-4xl font-black tracking-tight text-navy-800 dark:text-white sm:text-5xl">
            Telas pensadas para a velocidade do balcão
          </h2>
          <p className="mt-5 text-lg font-semibold text-navy-400 dark:text-navy-100/70">
            Interface intuitiva, sem termos técnicos complicados. Qualquer membro da equipe aprende em minutos.
          </p>
        </Reveal>

        <div className="space-y-24">
          {blocks.map((b) => (
            <Reveal
              key={b.n}
              delay={0.1}
              className={`grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-16 ${
                b.reverse ? "lg:[&>*:first-child]:order-2" : ""
              }`}
            >
              <div className="lg:col-span-6 space-y-6">
                <div className="flex items-center gap-3">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-navy-800 text-xs font-black text-white dark:bg-lavender dark:text-navy-900">
                    {b.n}
                  </span>
                  <span className="text-xs font-bold uppercase tracking-wider text-navy-400 dark:text-lavender">
                    {b.tag}
                  </span>
                </div>
                <h3 className="text-3xl font-black text-navy-800 dark:text-white sm:text-4xl">
                  {b.title}
                </h3>
                <p className="text-base font-medium leading-relaxed text-navy-400 dark:text-navy-100/70">
                  {b.desc}
                </p>
                <div className="space-y-2.5 pt-2">
                  {b.bullets.map((bullet, idx) => (
                    <div
                      key={idx}
                      className="flex items-start gap-2.5 text-xs font-bold text-navy-700 dark:text-navy-100"
                    >
                      <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500 mt-0.5" />
                      <span>{bullet}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="lg:col-span-6 flex justify-center">
                {b.media}
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Showcase;
